import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, Bot, Brain, Users, Timer, Zap, Sparkles, Crown, Plus } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useState } from "react";

// Feature flags - set to false to hide features while preserving code
const PLAY_ONLINE_ENABLED = false;
const HATCH_MODE_ENABLED = false;

interface MobileGameOptionsProps {
  onStartGame: (mode: 'classic' | 'hatch', opponent: string, isBot: boolean, elo?: number) => void;
  onStartEngine: () => void;
  onStartMultiplayer: () => void;
  onStartMatchmaking: () => void;
  onShowBotChallenge?: () => void;
}

export function MobileGameOptions({
  onStartGame,
  onStartEngine,
  onStartMultiplayer,
  onStartMatchmaking,
  onShowBotChallenge
}: MobileGameOptionsProps) {
  const [open, setOpen] = useState(false);

  const quickPlayBots = [
    { name: "Sophie", elo: 700, difficulty: "Beginner" },
    { name: "Nina", elo: 1200, difficulty: "Intermediate" },
    { name: "Jason", elo: 1400, difficulty: "Advanced" },
    { name: "Aisha", elo: 2000, difficulty: "Expert" }
  ];

  const timeControls = ["1+0", "3+0", "5+0", "10+0", "15+10", "30+0"];

  const handleAction = (action: () => void) => {
    setOpen(false);
    action();
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button 
          size="lg" 
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg z-50 bg-primary hover:bg-primary-hover"
        >
          <Plus className="w-6 h-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-80 p-4 space-y-4 overflow-y-auto">
        {/* Play Chess */}
        <Card className="border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Play className="w-5 h-5 text-primary" />
              Play Chess
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {PLAY_ONLINE_ENABLED && (
              <Button 
                onClick={() => handleAction(onStartMatchmaking)}
                className="w-full bg-primary hover:bg-primary-hover text-primary-foreground font-medium"
                size="lg"
              >
                <Users className="w-4 h-4 mr-2" />
                Play Online
              </Button>
            )}
            
            <Button 
              onClick={() => handleAction(onShowBotChallenge || (() => onStartGame('classic', 'Practice Bot', true, 800)))}
              variant="outline" 
              className="w-full"
              size="lg"
            >
              <Bot className="w-4 h-4 mr-2" />
              Play Computer
            </Button>
            
            <Button 
              onClick={() => handleAction(onStartEngine)}
              variant="outline" 
              className="w-full"
              size="lg"
            >
              <Brain className="w-4 h-4 mr-2" />
              Engine Challenge
            </Button>
          </CardContent>
        </Card>

        {/* Quick Bots */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Bot className="w-4 h-4" />
              Quick Play Bots
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {quickPlayBots.map((bot) => (
              <Button
                key={bot.name}
                variant="ghost"
                className="w-full justify-between h-auto p-3 hover:bg-muted/50"
                onClick={() => handleAction(() => onStartGame('classic', bot.name, true, bot.elo))}
              >
                <div className="flex flex-col items-start">
                  <span className="font-medium text-sm">{bot.name}</span>
                  <span className="text-xs text-muted-foreground">{bot.difficulty}</span>
                </div>
                <Badge variant="outline" className="text-xs">
                  {bot.elo}
                </Badge>
              </Button>
            ))}
          </CardContent>
        </Card>

        {/* Hatch Mode - Hidden for future launch */}
        {HATCH_MODE_ENABLED && (
          <Card className="border-hatch-glow/30 bg-gradient-to-br from-card to-hatch-glow/5">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Sparkles className="w-4 h-4 text-hatch-glow" />
                Hatch Mode
                <Badge className="bg-hatch-glow/20 text-hatch-glow text-xs">New!</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button
                variant="ghost"
                className="w-full justify-between h-auto p-3 hover:bg-hatch-glow/10"
                onClick={() => handleAction(() => onStartGame('hatch', 'Hatch Bot Alpha', true, 1200))}
              >
                <div className="flex flex-col items-start">
                  <span className="font-medium text-sm">Hatch Evolution</span>
                  <span className="text-xs text-muted-foreground">Pieces transform after captures</span>
                </div>
                <Crown className="w-4 h-4 text-hatch-glow" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Time Controls */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Timer className="w-4 h-4" />
              Time Controls
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {timeControls.map((time) => (
                <Button 
                  key={time} 
                  variant="outline" 
                  size="sm" 
                  className="text-xs"
                >
                  {time.includes('+') ? <><Zap className="w-3 h-3 mr-1" />{time}</> : time}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      </SheetContent>
    </Sheet>
  );
}